import GlobalContext from 'Contexts/GlobalContext'
import { ReactElement, useContext, useRef } from 'react'
import styled from 'styled-components'
import { formatPercent } from 'Utils'
import customMunicipalities from 'components/ChoroplethMap/Assets/municipalities_2023.json'
import CloseIcon from './CloseIcon'
import { de } from 'Globals/Locale'

window.nachnamen = window.nachnamen || {}
window.nachnamen.gdetop3 = window.nachnamen.gdetop3 || {}

interface Municipality {
  ORTNAME: string
  PLZ4: number
  PLZ6: number
  GDENAMK: string
  GDENR: number
  KTKZ: string
  NORMORTSNAME: string
  NORMGEMEINDE: string
}
const ToolTip: React.FC<{
  activeData: MunicipalityDataElement
  currentName?: string
  propertyName?: string
  isClosable?: boolean
  hasShadow?: boolean
  closePressed?: () => void
  sort?: 'rel' | 'abs'
}> = ({
  activeData,
  currentName,
  propertyName = 'abs',
  isClosable,
  hasShadow,
  closePressed,
  sort = 'rel',
}): ReactElement => {
  const { locale, tenant } = useContext(GlobalContext) ?? { locale: de }
  let lang = tenant?.lang || 'de'
  const activeMunicipality = (customMunicipalities as any).find(
    (muni: Municipality) => muni?.GDENR === activeData?.bfsId
  )

  const toolTipRef = useRef<HTMLDivElement>(null)

  if (!activeData) return <></>

  if (
    window.nachnamen &&
    window.nachnamen.gdetop3 &&
    window.nachnamen?.gdetop3[activeData.bfsId]
  ) {
    console.log(')))))))', window.nachnamen?.gdetop3[activeData.bfsId])
  }

  return (
    <ToolTipWrapper ref={toolTipRef} hasShadow={hasShadow}>
      {activeData && locale && (
        <>
          {
            <Head>
              {isClosable && (
                <ClosingButton
                  className='close-button'
                  onClick={(e) => {
                    e.stopPropagation()
                    closePressed?.()
                  }}
                >
                  <CloseIcon />
                </ClosingButton>
              )}
            </Head>
          }
          <Body>
            {propertyName === 'abs' && lang === 'de' && (
              <>
                <Row>
                  In <b>{activeMunicipality?.GDENAME}</b> kommt {currentName}{' '}
                  <b>{activeData.abs} mal</b> vor.
                </Row>
                <Row className='sub'>
                  Das sind <b>{formatPercent(activeData.rel)}</b> aller
                  Einwohnerinnen und Einwohner dieser Gemeinde.
                </Row>
              </>
            )}
            {propertyName === 'abs' && lang === 'fr' && (
              <>
                <Row>
                  À <b>{activeMunicipality?.GDENAME}</b>, le nom {currentName}{' '}
                  apparaît <b>{activeData.abs} fois</b>.
                </Row>
                <Row className='sub'>
                  Cela représente <b>{formatPercent(activeData.rel)}</b> de tous
                  les habitants et habitantes de cette commune.
                </Row>
              </>
            )}
            {propertyName === 'rel' && lang === 'de' && (
              <>
                <Row>
                  In <b>{activeMunicipality?.GDENAME}</b> heissen{' '}
                  <b>{formatPercent(activeData.rel)}</b> der Einwohnerinnen und
                  Einwohner <b>{currentName}</b>.
                </Row>
                <Row className='sub'>
                  Das sind <b>{activeData.abs}</b> Personen
                </Row>
              </>
            )}
            {propertyName === 'rel' && lang === 'fr' && (
              <>
                <Row>
                  A <b>{activeMunicipality?.GDENAME}</b>,{' '}
                  <b>{formatPercent(activeData.rel)}</b> des habitant-e-s
                  s'appellent <b>{currentName}</b>.
                </Row>
                <Row className='sub'>
                  Cela représente <b>{activeData.abs}</b> personnes.
                </Row>
              </>
            )}
            {window.nachnamen &&
              window.nachnamen.gdetop3 &&
              window.nachnamen?.gdetop3[activeData.bfsId] && (
                <GdeTop3>
                  {lang === 'de'
                    ? 'Die drei häufigsten Nachnamen in der Gemeinde:'
                    : 'Les trois noms de famille les plus fréquents dans la commune:'}
                  <table>
                    <tbody>
                      <>
                        {window.nachnamen?.gdetop3[activeData.bfsId].map(
                          (n: string, i: number) => {
                            var parts = n.split('%')
                            return (
                              <tr key={'tooltiptable_' + i}>
                                <td className='left'>{parts[0]}</td>
                                <td
                                  className={
                                    'right' + (sort === 'abs' ? ' sort' : '')
                                  }
                                >
                                  {parts[1]}
                                </td>
                                <td
                                  className={
                                    'right' + (sort === 'rel' ? ' sort' : '')
                                  }
                                >
                                  {formatPercent(+parts[2])}
                                </td>
                              </tr>
                            )
                          }
                        )}
                      </>
                    </tbody>
                  </table>
                </GdeTop3>
              )}
          </Body>
        </>
      )}
    </ToolTipWrapper>
  )
}

const GdeTop3 = styled.div`
  padding-top: 15px;
  border-top: 1px solid var(--gray-4);
  margin-top: 15px;
  table {
    table-layout: fixed;
    line-height: 1.2em;
    margin-left: auto;
    margin-right: auto;

    td {
      &.left {
        text-align: right;
        padding-right: 10px;
      }
      &.right {
        width: 60px;
        text-align: right;
        padding-left: 10px;

        &.sort {
          font-weight: 700;
        }
      }
    }
  }
`

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 1em;
  margin-bottom: 0;
  h4 {
    font-size: 1em;
    font-weight: 400;
    margin: 0;
    text-align: center;
    width: 100%;
  }
`
const Body = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1em;
  margin-top: 0;
`
const Row = styled.div`
  font-size: 1.3em;
  &.sub {
    font-size: 1em;
  }
`

const ClosingButton = styled.button`
  pointer-events: all;
  font-size: 1.5rem;
  line-height: 1;
  padding: 0.5rem;
  border: none;
  background-color: transparent;
  // disable flashing when pressing button
  -webkit-tap-highlight-color: transparent;
  color: inherit;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  &:hover {
    background-color: var(--color-black);
    color: var(--color-white);
  }
`

export const ToolTipWrapper = styled.div.attrs({ className: 'TT' })<{
  hasShadow?: boolean
}>`
  pointer-events: none;
  background-color: var(--site-background);
  margin-left: auto;
  margin-right: auto;
  padding: 15px;
  display: flex;
  flex-direction: column;
  text-align: center;
  border-radius: 1rem;
  font-size: 14px;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 5px 15px;
  font-family: var(--font-sans-serif);
  min-width: 250px;
  max-width: 450px;
  animation: slide-in-top 0.3s ease-in-out;
  @keyframes slide-in-top {
    0% {
      transform: translate3d(0, -80px, 0);
      opacity: 0;
      max-height: 0;
    }
    100% {
      transform: translate3d(0, 0, 0);
      opacity: 1;
      max-height: 400px;
    }
  }
`
export default ToolTip
